<template>
    <v-card>
        <v-card-title>
            <v-row>
                <v-col>
                    <v-icon
                        v-if="this.displayV1EntityList"
                        color="orange"
                        size="large"
                        style="margin-right: 10px"
                        >$warning</v-icon
                    >
                    {{ getTitle() }}
                </v-col>
                <v-spacer />
                <v-col class="shrink text-right">
                    <div
                        v-if="isAuthorized('Experiments:Delete') && this.selectedItems.length > 0"
                        style="display: inline-block"
                    >
                        <v-btn
                            @click="showDeleteThingsDialog"
                            color="error"
                            style="box-shadow: none; margin-right: 10px"
                        >
                            Delete {{ this.thingSchema.displayName }} Entities
                            <v-icon end>$delete</v-icon>
                        </v-btn>
                    </div>
                    <div
                        v-if="isAuthorized('Experiments:Update') && !this.displayV1EntityList"
                        style="display: inline-block"
                    >
                        <v-btn
                            @click="downloadSummaryOneTimeMeasurementSetExcel"
                            color="info"
                            style="box-shadow: none; margin-right: 10px"
                        >
                            Download Excel
                            <v-icon end>$download</v-icon>
                        </v-btn>
                        <v-btn
                            @click="showUploadSetupData"
                            color="info"
                            style="box-shadow: none; margin-right: 10px"
                        >
                            Upload Setup Data
                            <v-icon end>$upload</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="isAuthorized('MaterialSamples:Create')"
                            @click="openMaterialSamplesDialog"
                            color="info"
                            style="box-shadow: none; margin-right: 10px"
                            :disabled="this.selectedItems.length === 0"
                        >
                            Create Material Samples
                            <v-icon end>$plus</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="isAuthorized('MaterialSamples:Create')"
                            @click="openSamplesAndRequestDialog"
                            color="info"
                            style="box-shadow: none; margin-right: 10px"
                            :disabled="this.selectedItems.length === 0"
                        >
                            Create Samples & Request
                            <v-icon end>$plus</v-icon>
                        </v-btn>
                        <v-btn @click="viewV1EntityData">View V1 Data</v-btn>
                    </div>
                    <div
                        v-if="isAuthorized('Experiments:Update') && this.displayV1EntityList"
                        style="display: inline-block"
                    >
                        <v-btn
                            @click="downloadV1CustomEntitySetupExcel"
                            color="info"
                            style="box-shadow: none; margin-right: 10px"
                        >
                            Download V1 Excel
                            <v-icon end>$download</v-icon>
                        </v-btn>
                        <v-btn @click="viewMeasurementsEntityData">View Measurements Data</v-btn>
                    </div>
                </v-col>
            </v-row>
        </v-card-title>

        <entity-list
            v-if="displayV1EntityList"
            :experiment="experiment"
            :protocol="protocol"
            :thing-schema="thingSchema"
            :search="search"
            :is-using-measurements="true"
        />

        <v-data-table
            v-if="!displayV1EntityList"
            :headers="thingHeaders"
            :items="thingRowItems"
            :no-results-text="`No ${thingSchema.displayName} found`"
            :search="search"
            show-select
            multi-select
            return-object
            v-model="selectedItems"
        >
            <template v-slot:item.edit="{ item }">
                <v-icon v-if="isAuthorized('ExperimentData:Update')" @click="openEditDialog(item)"
                    >$pencil</v-icon
                >
            </template>
            <template v-slot:item.conditionName="{ item }">
                <span style="white-space: nowrap">
                    {{ item.conditionName }}
                </span>
            </template>
            <template
                v-for="slot in thingMeasurementTableSlots"
                v-slot:[`item.${slot.attribute.name}`]="{ item }"
                v-bind:key="slot.attribute.name"
            >
                <div>
                    {{ getMeasurementValue(item, slot.attribute) }}
                </div>
            </template>
        </v-data-table>
        <v-dialog v-model="editThingMeasurementsDialogVisible">
            <edit-thing-measurements-form
                v-if="selectedThing != null"
                :thingSchema="thingSchema"
                :thing="selectedThing"
                :measurements="selectedThingMeasurements"
                :data-collection-task="defaultDataCollectionTask"
                :task-schedule="taskSchedule"
                :experiment="experiment"
                :protocol="protocol"
                @save="saveUserMeasurementSet"
                @closed="closeEditDialog"
            />
        </v-dialog>
        <v-dialog v-model="uploadSetupDataDialogVisible">
            <import-setup-measurements-form
                :experiment="experiment"
                :thing-schema="thingSchema"
                @canceled="closeImportSetupData"
                @saved="savedImportSetupData"
            />
        </v-dialog>
        <v-dialog v-model="deleteThingsWarningVisible">
            <v-card>
                <v-card-title style="background-color: red; color: white"
                    ><v-icon color="white">$alert</v-icon> &nbsp; Are you sure you want to delete
                    the following {{ this.selectedItems.length }}
                    {{ this.thingSchema.displayName }} entities:
                </v-card-title>
                <v-card-text>
                    <v-alert class="mt-5" style="background-color: red; color: white">
                        These {{ this.thingSchema.displayName }} Entities will be permanently
                        deleted. This cannot be undone.
                    </v-alert>
                    <v-data-table :items="selectedItems" :headers="selectedItemHeaders">
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="closeDeleteThingsDialog">Cancel</v-btn>
                    <v-btn color="error" @click="deleteThings">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="createSamplesAndRequestDialogVisible" max-width="600px">
            <samples-and-request-form
                :experiment="experiment"
                :things="selectedItems"
                :sops="sampleCollectionSOPs"
                :request-protocols="requestProtocols"
                @canceled="createSamplesAndRequestDialogVisible = false"
                @saved="createSamplesAndRequestDialogVisible = false"
                @configureSamples="startSamplesConfiguration"
            />
        </v-dialog>
        <v-dialog v-model="createMaterialSamplesDialogVisible" max-width="600px">
            <material-samples-form
                class="mt-4"
                v-model="materialSamplesContext"
                v-model:valid="samplesValid"
                :experiment="experiment"
                :things="selectedItems"
                :sops="sampleCollectionSOPs"
                :embedded="false"
                @canceled="createMaterialSamplesDialogVisible = false"
                @saved="createMaterialSamplesDialogVisible = false"
            />
        </v-dialog>
    </v-card>
</template>
<script>
import { withLoadingScreen } from "@/lib/loading";
import { buildDataCollectionTasksAPIForExperiment, measurementsAPI } from "@/services/api.services";
import {
    buildMeasurementsImportMessage,
    findMeasurementForAttributeName,
    findThingMeasurementSet,
    getLatestMeasurementForThingAttribute,
    getMeasurementDisplayValue,
} from "@/lib/measurementsUtils";
import { mapGetters } from "vuex";
import {
    downloadCustomEntitySetupExcel,
    downloadSummaryOneTimeMeasurementSetExcel,
    getOneTimeAttributesList,
    getParentThingSchema,
    isConditionAvailableOnThingsForSchema,
    isTopLevelSchema,
} from "@/lib/thingUtils";
import EditThingMeasurementsForm from "../Measurements/EditThingMeasurementsForm.vue";
import { defineComponent } from "vue";
import EntityList from "./EntityList.vue";
import ImportSetupMeasurementsForm from "../Measurements/ImportSetupMeasurementsForm.vue";
import SamplesAndRequestForm from "@/components/SamplesV2/SamplesAndRequestForm.vue";
import MaterialSamplesForm from "../SamplesV2/MaterialSamplesForm.vue";
import { isSchemaNameDisplayDifferent } from "@/lib/schemaUtils";

import { useToast } from "vue-toastification";
import { nullSafeSortDates, nullSafeSortNumbers, nullSafeSortStrings } from "@/lib/arrayUtils";
import _ from "lodash";
import { thingAPI } from "@/services/things/thingAPI";
import { samplesAPI } from "@/services/samples/samplesAPI";

export default defineComponent({
    components: {
        ImportSetupMeasurementsForm,
        EntityList,
        EditThingMeasurementsForm,
        MaterialSamplesForm,
        SamplesAndRequestForm,
    },

    props: {
        protocol: {
            type: Object, // AbstractSchemaConfig
            required: false,
        },
        experiment: {
            type: Object,
            required: true,
        },
        thingSchema: {
            type: Object,
            required: true,
        },
        taskSchedule: {
            type: Object,
            required: true,
        },
        search: {
            type: String,
            default: undefined,
        },
    },

    setup() {
        return {
            toast: useToast(),
        };
    },

    data() {
        return {
            dataCollectionTaskApi: null,
            currentUserMeasurementSet: null,
            oneTimeAttributesList: [],
            things: [],
            thingHeaders: [],
            thingRowItems: [],
            thingMeasurementTableSlots: [],
            summaryOneTimeMeasurementSets: [],
            editThingMeasurementsDialogVisible: false,
            selectedThing: null,
            selectedThingMeasurements: [],
            defaultDataCollectionTask: null,
            displayV1EntityList: false,
            uploadSetupDataDialogVisible: false,
            deleteThingsWarningVisible: false,
            createMaterialSamplesDialogVisible: false,
            createSamplesAndRequestDialogVisible: false,
            selectedItems: [],
            selectedItemHeaders: [{}],
            sampleCollectionSOPs: [],
            requestProtocols: [],
            materialSamplesContext: {
                experimentCode: null,
                things: null,
                descriptors: [],
                materialType: null,
                needByDate: null,
                collectionSOP: null,
            },
            samplesValid: false,
        };
    },

    watch: {
        thingSchema: {
            handler() {
                this.loadThings();
                this.selectedItems = [];
            },
            immediate: true,
        },
    },

    async mounted() {
        this.dataCollectionTaskApi = buildDataCollectionTasksAPIForExperiment(this.experiment.code);
        this.sampleCollectionSOPs = await samplesAPI.fetchSampleSOPs();
        this.requestProtocols = await samplesAPI.fetchRequestProtocols();
    },

    computed: {
        ...mapGetters({
            isAuthorized: "isAuthorized",
            user: "getAuthUser",
        }),
        thingIdsToDelete() {
            return this.selectedItems.map((item) => item.id);
        },
    },

    methods: {
        getTitle() {
            const altName = isSchemaNameDisplayDifferent(this.thingSchema)
                ? ` (${this.thingSchema.name})`
                : "";
            if (this.displayV1EntityList) {
                return `${this.thingSchema.displayName}${altName} Entities (V1 Data)`;
            } else {
                return `${this.thingSchema.displayName}${altName} Entities`;
            }
        },
        downloadSummaryOneTimeMeasurementSetExcel() {
            downloadSummaryOneTimeMeasurementSetExcel({
                experimentCode: this.experiment.code,
                thingSchemaId: this.thingSchema.id,
            });
        },
        downloadV1CustomEntitySetupExcel() {
            downloadCustomEntitySetupExcel({
                protocolCode: this.protocol.code,
                experimentCode: this.experiment.code,
                entityName: this.thingSchema.name,
                entityParent: this.thingSchema.parentThingSchemaName,
            });
        },
        showUploadSetupData() {
            this.uploadSetupDataDialogVisible = true;
        },
        closeImportSetupData() {
            this.uploadSetupDataDialogVisible = false;
        },
        openMaterialSamplesDialog() {
            this.materialSamplesContext.experimentCode = this.experiment.code;
            this.materialSamplesContext.things = this.selectedItems;
            this.createMaterialSamplesDialogVisible = true;
        },
        openSamplesAndRequestDialog() {
            this.createSamplesAndRequestDialogVisible = true;
        },
        savedImportSetupData(result) {
            const { skippedRows, skippedAttributes } = result;
            const msg = buildMeasurementsImportMessage({ skippedRows, skippedAttributes });
            const toastOpts =
                skippedRows.length > 0 || skippedAttributes > 0 ? { timeout: false } : {};
            this.toast.success(
                `Setup data uploaded for ${this.thingSchema.displayName}\n${msg}`,
                toastOpts
            );
            this.closeImportSetupData();
            this.loadThings();
        },
        async loadThings() {
            await withLoadingScreen(async () => {
                this.displayV1EntityList = false;
                // Note: If we don't clear these, the table will re-render _while_ we're loading the new data,
                // throwing errors because the table is trying to render an old Thing instance against a new registrySchema.
                this.thingRowItems = [];

                // While we could fetch these when mounted, we want to be able to refresh the data when the
                // form changes, ensuring we have the latest summary data from the field. It's probably unlikely
                // that field data is coming in _at the same time_ the admin is making edits, and even less likely
                // that the admin is making edits to the same data as the field users.
                this.summaryOneTimeMeasurementSets =
                    await measurementsAPI.fetchSummaryOneTimeMeasurementSets(this.experiment.code);

                this.oneTimeAttributesList = getOneTimeAttributesList(this.thingSchema);
                this.things = await thingAPI.fetchThingsForSchema(
                    this.protocol?.code || null,
                    this.experiment.code,
                    this.thingSchema.id
                );

                this.defaultDataCollectionTask = this.taskSchedule.defaultDataCollectionTask;
                if (!this.defaultDataCollectionTask) {
                    // TODO: For now, create one here, but this should move to experiment setup time.
                    this.defaultDataCollectionTask =
                        await this.dataCollectionTaskApi.createNewDefaultTask(this.protocol.code);
                }

                this.currentUserMeasurementSet =
                    await measurementsAPI.fetchMyUserMeasurementSetForDataCollectionTaskId(
                        this.defaultDataCollectionTask.id
                    );
                if (!this.currentUserMeasurementSet) {
                    this.currentUserMeasurementSet = {
                        experimentCode: this.experiment.code,
                        dataCollectionTaskId: this.defaultDataCollectionTask.id,
                        userId: this.user.email,
                        thingMeasurementSets: [],
                    };
                }

                this.buildTable();
            });
        },
        viewV1EntityData() {
            this.displayV1EntityList = true;
        },
        viewMeasurementsEntityData() {
            this.displayV1EntityList = false;
        },
        buildCommonThingHeaders() {
            const thingHeaders = [
                {
                    title: "name",
                    value: "name",
                    sortable: true,
                    width: "10%",
                    align: "left",
                },
                {
                    title: "id",
                    value: "id",
                    sortable: true,
                    width: "10%",
                    align: "left",
                },
            ];

            if (!isTopLevelSchema(this.thingSchema)) {
                const parentThingSchema = getParentThingSchema(this.thingSchema, this.protocol);
                thingHeaders.push({
                    title: `Parent ${parentThingSchema.displayName}`,
                    value: "parentThingDisplayName",
                    sortable: true,
                    width: "10%",
                    align: "left",
                });
            }

            if (isConditionAvailableOnThingsForSchema(this.thingSchema, this.protocol)) {
                thingHeaders.push(
                    {
                        title: "Condition Number",
                        value: "conditionNumber",
                        sortable: true,
                        width: "1%",
                        align: "left",
                    },
                    {
                        title: "Condition Name",
                        value: "conditionName",
                        sortable: true,
                        width: "10%",
                        align: "left",
                    }
                );
            }

            if (this.thingSchema.hasWellPosition) {
                thingHeaders.push(
                    {
                        title: "Container Name",
                        value: "containerName",
                        sortable: true,
                        width: "10%",
                        align: "left",
                    },
                    {
                        title: "Position",
                        value: "wellPosition",
                        sortable: true,
                        width: "10%",
                        align: "left",
                    }
                );
            }

            return thingHeaders;
        },
        buildTable() {
            this.selectedItemHeaders = this.buildCommonThingHeaders();
            this.thingHeaders = this.buildCommonThingHeaders();

            if (this.isAuthorized("ExperimentData:Update")) {
                this.thingHeaders.splice(0, 0, {
                    title: "edit",
                    value: "edit",
                    sortable: false,
                    width: "1%",
                    align: "left",
                });
            }

            const size = 100 / this.oneTimeAttributesList.length;

            this.oneTimeAttributesList.forEach((attribute) => {
                const sortByAttributeFn = (rowItemA, rowItemB) => {
                    const a = this.getMeasurementValue(rowItemA, attribute);
                    const b = this.getMeasurementValue(rowItemB, attribute);
                    if (typeof a === "number") {
                        return nullSafeSortNumbers(a, b);
                    } else if (_.isDate(a)) {
                        return nullSafeSortDates(a, b);
                    } else {
                        return nullSafeSortStrings(a, b);
                    }
                };

                this.thingHeaders.push({
                    title: attribute.display_name,
                    value: attribute.name,
                    sortable: true,
                    sortRaw: sortByAttributeFn,
                    width: `${size}%`,
                    align: "left",
                });

                this.thingMeasurementTableSlots.push({
                    attribute: attribute,
                });
            });

            this.thingRowItems = this.things.map((thing) => {
                return this.buildThingRowItem(thing);
            });
        },
        buildThingRowItem(thing) {
            const thingRowItem = {
                id: thing.id,
                name: thing.displayName,
                parentThingId: thing.parentThingId,
                parentThingDisplayName: thing.parentThingDisplayName,
                thing: thing,
                conditionName: thing.condition?.name || "",
                conditionNumber: thing.condition?.number || "",
                containerName: thing.containerWellPosition?.containerName || "",
                wellPosition: thing.containerWellPosition?.wellPosition || "",
                /**
                 * Could be UserMeasurement or a summary Measurement for each attribute
                 */
                measurements: [],
            };

            this.oneTimeAttributesList.forEach((attribute) => {
                this.updateThingRowItemForAttribute(thingRowItem, attribute);
            });

            return thingRowItem;
        },
        updateThingRowItemForAttribute(thingRowItem, attribute) {
            const thing = thingRowItem.thing;
            // TODO: this is likely already calculated on the server, right? Why bother doing it here?
            const measurement = getLatestMeasurementForThingAttribute({
                thingSchema: this.thingSchema,
                thing,
                attribute,
                summaryOneTimeMeasurementSets: this.summaryOneTimeMeasurementSets,
                currentUserMeasurementSet: this.currentUserMeasurementSet,
            });

            if (measurement) {
                thingRowItem.measurements.push(measurement);
            }
        },
        getMeasurementValue(thingRowItem, attribute) {
            const attributeName = attribute.name;
            const { measurements } = thingRowItem;

            const measurement = findMeasurementForAttributeName(attributeName, measurements);
            if (!measurement) {
                return null;
            }

            return getMeasurementDisplayValue(
                measurement.value,
                attributeName,
                this.thingSchema,
                this.experiment,
                this.protocol
            );
        },
        openEditDialog(item) {
            this.editThingMeasurementsDialogVisible = true;
            this.selectedThing = item.thing;
            this.selectedThingMeasurements = item.measurements;
        },
        closeEditDialog() {
            this.editThingMeasurementsDialogVisible = false;
            this.selectedThing = null;
            this.selectedThingMeasurements = [];
        },
        async saveUserMeasurementSet(updatedUserMeasurementSet) {
            await withLoadingScreen(async () => {
                return this.save(updatedUserMeasurementSet);
            });
            this.closeEditDialog();
            await this.loadThings();
        },
        async save(measurements) {
            const thing = this.selectedThing;
            const existingThingMeasurementSet = findThingMeasurementSet(
                this.currentUserMeasurementSet,
                thing
            );
            if (existingThingMeasurementSet) {
                existingThingMeasurementSet.measurements = measurements;
            } else {
                this.currentUserMeasurementSet.thingMeasurementSets.push({
                    thingId: thing.id,
                    measurements,
                });
            }
            const savedUserMeasurementSet = await measurementsAPI.saveUserMeasurementSet(
                this.currentUserMeasurementSet
            );
            this.toast.success(`${this.thingSchema.displayName} ${thing.displayName} updated`);
            console.log(`saved UserMeasurementSet`, savedUserMeasurementSet);
        },
        showDeleteThingsDialog() {
            if (this.selectedItems.length === 0) {
                return;
            }

            this.deleteThingsWarningVisible = true;
        },
        closeDeleteThingsDialog() {
            this.deleteThingsWarningVisible = false;
        },
        async deleteThings() {
            await withLoadingScreen(async () => {
                await thingAPI.deleteThings(this.experiment.code, this.thingIdsToDelete);
                this.selectedItems = [];
                this.closeDeleteThingsDialog();
                await this.loadThings();
            });
        },
        startSamplesConfiguration(payload) {
            this.createMaterialSamplesDialogVisible = false;
            this.$emit("configureSamples", payload);
        },
    },
});
</script>
<style></style>
